import { useEffect } from 'react';
import { SERVICES, ViewState } from './types';

const DEFAULT_SITE_URL = 'https://mierbeautysalon.skycresce.com';
const SITE_NAME = 'MIER Beauty Lounge';
const DEFAULT_IMAGE = '/hero.png';

export const BUSINESS = {
  name: SITE_NAME,
  legalName: SITE_NAME,
  url: DEFAULT_SITE_URL,
  telephone: '+971543446466',
  whatsapp: 'https://api.whatsapp.com/send/?phone=971543446466',
  email: 'hello@mierlounge.ae',
  addressLine: 'Aloft Hotel Ground Floor, Al Rawdah W59 01',
  city: 'Abu Dhabi',
  countryCode: 'AE',
  countryName: 'UAE',
  areaServed: ['Abu Dhabi', 'Al Rawdah', 'ADNEC area', 'United Arab Emirates'],
  openingHours: ['Mo-Su 10:00-22:00'],
  sameAs: ['https://www.instagram.com/mier.lounge/'],
};

type SeoConfig = {
  title: string;
  titleAr?: string;
  description: string;
  descriptionAr?: string;
  keywords: string;
  keywordsAr?: string;
  ogType?: 'website' | 'article';
  schema?: any[];
};

type BreadcrumbItem = {
  name: string;
  path: string;
};

const homeFaq = [
  {
    question: 'What is the best beauty salon near ADNEC Abu Dhabi?',
    answer:
      'MIER Beauty Lounge is a luxury ladies salon at Aloft Hotel Ground Floor in Al Rawdah, Abu Dhabi, minutes from ADNEC, offering hair, nails, facials, mesotherapy, lash, brow, and bridal beauty services.',
  },
  {
    question: 'How much does a nail extension refill cost in Abu Dhabi?',
    answer:
      'Nail extension refill prices in Abu Dhabi vary by product and length. At MIER Beauty Lounge, nail extension refills start from AED 210 using premium salon-grade systems for long-lasting wear.',
  },
  {
    question: 'Are there ladies-only salons in Al Rawdah Abu Dhabi?',
    answer:
      'Yes. MIER Beauty Lounge is a ladies-focused beauty salon in Al Rawdah, Abu Dhabi, designed for private, comfortable appointments across hair, nails, lashes, brows, skincare, and bridal beauty.',
  },
  {
    question: 'How long do lash extensions last in Abu Dhabi weather?',
    answer:
      'Lash extensions usually last around 3 to 4 weeks in Abu Dhabi. Heat and humidity can shorten retention, so oil-free cleansing and regular refills are recommended.',
  },
];

const servicesFaq = [
  {
    question: 'Where can I book keratin treatment in Abu Dhabi?',
    answer:
      'You can book keratin treatment at MIER Beauty Lounge in Al Rawdah, Abu Dhabi. The salon is located at Aloft Hotel near ADNEC and offers smoothing treatments, color services, nails, and facials.',
  },
  {
    question: 'Do you offer mesotherapy in Abu Dhabi?',
    answer:
      'Yes. MIER Beauty Lounge offers mesotherapy in Abu Dhabi as part of its aesthetics and skin treatment menu, alongside facials, lash services, brow services, and bridal beauty.',
  },
  {
    question: 'How much do gel nails cost in Abu Dhabi?',
    answer:
      'Gel manicure prices depend on product type and add-ons. At MIER Beauty Lounge in Abu Dhabi, gel manicures start from AED 200, and builder gel extensions start from AED 320.',
  },
];

const aboutFaq = [
  {
    question: 'Where is MIER Beauty Lounge located?',
    answer:
      'MIER Beauty Lounge is located on the ground floor of Aloft Hotel, Al Rawdah W59 01, Abu Dhabi, UAE, close to ADNEC and the surrounding hotel and exhibition district.',
  },
];

const breadcrumbSchema = (items: BreadcrumbItem[], siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: new URL(item.path, siteUrl).toString(),
  })),
});

const faqSchema = (faqs: typeof homeFaq) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

const serviceCatalogSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'OfferCatalog',
  name: 'Beauty Services',
  itemListElement: SERVICES.map((service) => ({
    '@type': 'Offer',
    priceCurrency: 'AED',
    priceSpecification: {
      '@type': 'PriceSpecification',
      priceCurrency: 'AED',
      price: service.price.replace(/[^0-9]/g, '') || undefined,
    },
    itemOffered: {
      '@type': 'Service',
      name: service.name,
      description: service.description,
      areaServed: BUSINESS.city,
      provider: {
        '@type': 'BeautySalon',
        name: BUSINESS.name,
      },
    },
  })),
});

const beautySalonSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BeautySalon',
  name: BUSINESS.name,
  legalName: BUSINESS.legalName,
  url: siteUrl,
  image: new URL(DEFAULT_IMAGE, siteUrl).toString(),
  telephone: BUSINESS.telephone,
  email: BUSINESS.email,
  priceRange: 'AED 80 - AED 1200',
  address: {
    '@type': 'PostalAddress',
    streetAddress: BUSINESS.addressLine,
    addressLocality: BUSINESS.city,
    addressCountry: BUSINESS.countryCode,
  },
  openingHours: BUSINESS.openingHours,
  areaServed: BUSINESS.areaServed,
  sameAs: BUSINESS.sameAs,
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    reviewCount: '1842',
  },
  hasOfferCatalog: serviceCatalogSchema(),
});

const websiteSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: siteUrl,
  inLanguage: ['en-AE', 'ar-AE'],
});

const organizationSchema = (siteUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: siteUrl,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: BUSINESS.telephone,
    contactType: 'customer service',
    areaServed: 'AE',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: BUSINESS.sameAs,
});

export const routeSeo: Record<ViewState, Omit<SeoConfig, 'path'>> = {
  home: {
    title: 'MIER. | Luxury Salon in Abu Dhabi | Al Rawdah',
    titleAr: 'مير. | صالون تجميل فاخر في أبوظبي | الروضة',
    description: 'MIER Beauty Lounge is a luxury beauty salon in Abu Dhabi at Aloft Hotel Ground Floor near ADNEC. Premium hair, nails, skin treatments, and bridal beauty.',
    descriptionAr: 'مير بيوتي لونج هو صالون تجميل فاخر في أبوظبي في الطابق الأرضي من فندق ألوفت بالقرب من أدنيك. خدمات متميزة للشعر والأظافر وعلاجات البشرة وجمال العرائس.',
    keywords: 'beauty salon Abu Dhabi, luxury salon Abu Dhabi, ladies salon Al Rawdah, salon near ADNEC Abu Dhabi',
    keywordsAr: 'صالون تجميل أبوظبي، صالون فاخر أبوظبي، صالون نسائي الروضة، صالون بالقرب من أدنيك أبوظبي',
  },
  services: {
    title: 'Beauty Services Abu Dhabi | Hair, Nails & Skin | MIER',
    titleAr: 'خدمات التجميل أبوظبي | شعر، أظافر وبشرة | مير',
    description: 'Explore our full menu of beauty services in Abu Dhabi, including professional hair styling, gel nails, mesotherapy, and bridal packages.',
    descriptionAr: 'اكتشفي قائمة خدمات التجميل الكاملة لدينا في أبوظبي، بما في ذلك تصفيف الشعر الاحترافي، أظافر الجل، الميزوثيرابي، وباقات العرائس.',
    keywords: 'hair salon Abu Dhabi, nail salon Abu Dhabi, skin treatments Abu Dhabi',
  },
  'hair-salon': {
    title: 'Hair Salon Abu Dhabi | Keratin, Coloring & Cuts | MIER',
    titleAr: 'صالون شعر أبوظبي | كيراتين، صبغة وقص | مير',
    description: 'Expert hair salon in Abu Dhabi offering keratin treatments, balayage, hair coloring, and precision cuts at Aloft Hotel Al Rawdah.',
    descriptionAr: 'صالون شعر خبير في أبوظبي يقدم علاجات الكيراتين، البلياج، صبغة الشعر، وقصات دقيقة في فندق ألوفت الروضة.',
    keywords: 'hair salon Abu Dhabi, keratin treatment Abu Dhabi, balayage Abu Dhabi, hair colorist Abu Dhabi',
  },
  'nail-salon': {
    title: 'Nail Salon Abu Dhabi | Gel Nails & Extensions | MIER',
    titleAr: 'صالون أظافر أبوظبي | أظافر جل وتركيب | مير',
    description: 'Premium nail salon in Abu Dhabi. Specialized in gel nails, nail extensions, nail art, and luxury manicures in Al Rawdah near ADNEC.',
    descriptionAr: 'صالون أظافر متميز في أبوظبي. متخصصون في أظافر الجل، تركيب الأظافر، فن الأظافر، والمناكير الفاخر في الروضة بالقرب من أدنيك.',
    keywords: 'nail salon Abu Dhabi, gel nails Abu Dhabi, nail extension Abu Dhabi, nail art Abu Dhabi',
  },
  'skin-treatments': {
    title: 'Skin Treatments & Mesotherapy Abu Dhabi | MIER',
    titleAr: 'علاجات البشرة والميزوثيرابي أبوظبي | مير',
    description: 'Revitalize your skin with mesotherapy, fillers, and advanced facials in Abu Dhabi at MIER Beauty Lounge, Aloft Hotel.',
    descriptionAr: 'جددي بشرتك مع الميزوثيرابي والفيلر والفيشل المتقدم في أبوظبي في مير بيوتي لونج، فندق ألوفت.',
    keywords: 'mesotherapy Abu Dhabi, skin treatment Abu Dhabi, fillers Abu Dhabi, facial Abu Dhabi',
  },
  'lash-brow': {
    title: 'Eyelash Extensions & Lash Lift Abu Dhabi | MIER',
    titleAr: 'تركيب رموش ورفع رموش أبوظبي | مير',
    description: 'Enhance your look with premium eyelash extensions, lash lifts, and brow lamination in Abu Dhabi at MIER Beauty Lounge.',
    descriptionAr: 'عززي إطلالتك مع تركيب الرموش الفاخر، رفع الرموش، وتغليف الحواجب في أبوظبي في مير بيوتي لونج.',
    keywords: 'eyelash extension Abu Dhabi, lash lift Abu Dhabi, brow lamination Abu Dhabi',
  },
  bridal: {
    title: 'Bridal Makeup & Hair Abu Dhabi | Wedding Packages | MIER',
    titleAr: 'مكياج وشعر عرائس أبوظبي | باقات الزفاف | مير',
    description: 'Luxury bridal makeup and hair styling in Abu Dhabi. Custom wedding beauty packages at Aloft Hotel Ground Floor.',
    descriptionAr: 'مكياج وشعر عرائس فاخر في أبوظبي. باقات جمال زفاف مخصصة في الطابق الأرضي من فندق ألوفت.',
    keywords: 'bridal makeup Abu Dhabi, wedding hair Abu Dhabi, bridal package UAE',
  },
  'location-al-rawdah': {
    title: 'Beauty Salon Al Rawdah Abu Dhabi | MIER Beauty Lounge',
    titleAr: 'صالون تجميل الروضة أبوظبي | مير بيوتي لونج',
    description: 'Looking for a ladies salon in Al Rawdah? MIER Beauty Lounge offers premium hair, nail, and skin services near Al Rawdah Park.',
    descriptionAr: 'تبحثين عن صالون نسائي في الروضة؟ يقدم مير بيوتي لونج خدمات متميزة للشعر والأظافر والبشرة بالقرب من حديقة الروضة.',
    keywords: 'beauty salon Al Rawdah, ladies salon Al Rawdah, salon Al Rawdah Abu Dhabi',
  },
  'location-adnec': {
    title: 'Beauty Salon near ADNEC Abu Dhabi | MIER Beauty Lounge',
    titleAr: 'صالون تجميل بالقرب من أدنيك أبوظبي | مير بيوتي لونج',
    description: 'The closest luxury beauty lounge to ADNEC Abu Dhabi. Perfect for exhibition attendees and visitors seeking premium beauty services.',
    descriptionAr: 'أقرب صالون تجميل فاخر إلى أدنيك أبوظبي. مثالي لزوار المعارض والباحثين عن خدمات تجميل متميزة.',
    keywords: 'salon near ADNEC, beauty salon near Abu Dhabi National Exhibition Centre',
  },
  'location-aloft': {
    title: 'Beauty Salon Aloft Hotel Abu Dhabi | MIER Beauty Lounge',
    titleAr: 'صالون تجميل فندق ألوفت أبوظبي | مير بيوتي لونج',
    description: 'Visit MIER Beauty Lounge inside Aloft Hotel Abu Dhabi. Luxury hair, nail, and skin treatments for hotel guests and residents.',
    descriptionAr: 'زوري مير بيوتي لونج داخل فندق ألوفت أبوظبي. علاجات فاخرة للشعر والأظافر والبشرة لضيوف الفندق والمقيمين.',
    keywords: 'salon Aloft Hotel Abu Dhabi, beauty salon near Aloft Hotel',
  },
  about: {
    title: 'About MIER Beauty Lounge | Premium Abu Dhabi Salon',
    titleAr: 'حول مير بيوتي لونج | صالون متميز في أبوظبي',
    description: 'Learn about our mission to provide the most refined beauty experience in Abu Dhabi at our Aloft Hotel location.',
    descriptionAr: 'تعرفي على مهمتنا لتقديم أرقى تجربة جمال في أبوظبي في موقعنا بفندق ألوفت.',
    keywords: 'best salon Abu Dhabi, luxury beauty lounge UAE',
  },
  gallery: {
    title: 'Salon Portfolio Abu Dhabi | Hair & Nail Artistry | MIER',
    titleAr: 'معرض الصالون أبوظبي | فن الشعر والأظافر | مير',
    description: 'View our work including balayage transformations, nail extensions, and bridal looks created at MIER Beauty Lounge Abu Dhabi.',
    descriptionAr: 'شاهدي أعمالنا بما في ذلك تحولات البلياج، تركيب الأظافر، وإطلالات العرائس في مير بيوتي لونج أبوظبي.',
    keywords: 'nail art portfolio, hair salon gallery Abu Dhabi',
  },
  blog: {
    title: 'Abu Dhabi Beauty Blog | Tips & Trends 2025 | MIER',
    titleAr: 'مدونة الجمال في أبوظبي | نصائح واتجاهات 2025 | مير',
    description: 'Stay updated with the latest beauty trends in the UAE, from hair care tips to nail extension maintenance.',
    descriptionAr: 'ابقِ على اطلاع بأحدث اتجاهات الجمال في الإمارات، من نصائح العناية بالشعر إلى صيانة تركيب الأظافر.',
    keywords: 'beauty blog UAE, salon tips Abu Dhabi',
  },
  'blog-post': {
    title: 'Beauty Insight | MIER Beauty Lounge Abu Dhabi',
    titleAr: 'رؤية الجمال | مير بيوتي لونج أبوظبي',
    description: 'In-depth beauty guides and trends specifically for the Abu Dhabi lifestyle and climate.',
    descriptionAr: 'أدلة جمال متعمقة واتجاهات مخصصة لنمط الحياة والمناخ في أبوظبي.',
    keywords: 'beauty trends UAE, skincare tips Abu Dhabi',
  },
  booking: {
    title: 'Book Appointment | MIER Beauty Lounge Abu Dhabi',
    titleAr: 'حجز موعد | مير بيوتي لونج أبوظبي',
    description: 'Book your luxury hair, nail, or skin treatment at MIER Beauty Lounge Abu Dhabi near ADNEC.',
    descriptionAr: 'احجزي علاجك الفاخر للشعر أو الأظافر أو البشرة في مير بيوتي لونج أبوظبي بالقرب من أدنيك.',
    keywords: 'book salon Abu Dhabi, book nail appointment Abu Dhabi',
  },
};


export const pathForView = (view: ViewState) => {
  switch (view) {
    case 'services': return '/services';
    case 'hair-salon': return '/hair-salon-abu-dhabi';
    case 'nail-salon': return '/nail-salon-abu-dhabi';
    case 'skin-treatments': return '/skin-treatments-abu-dhabi';
    case 'lash-brow': return '/eyelash-extension-abu-dhabi';
    case 'bridal': return '/bridal-makeup-abu-dhabi';
    case 'location-al-rawdah': return '/beauty-salon-al-rawdah-abu-dhabi';
    case 'location-adnec': return '/salon-near-adnec-abu-dhabi';
    case 'location-aloft': return '/beauty-salon-aloft-hotel-abu-dhabi';
    case 'about': return '/about';
    case 'gallery': return '/gallery';
    case 'blog': return '/blog';
    case 'blog-post': return '/blog/post';
    case 'booking': return '/book';
    case 'home':
    default: return '/';
  }
};

export const viewFromPath = (path: string): ViewState => {
  if (path.startsWith('/services')) return 'services';
  if (path.startsWith('/hair-salon')) return 'hair-salon';
  if (path.startsWith('/nail-salon')) return 'nail-salon';
  if (path.startsWith('/skin-treatments')) return 'skin-treatments';
  if (path.startsWith('/eyelash-extension')) return 'lash-brow';
  if (path.startsWith('/bridal-makeup')) return 'bridal';
  if (path.startsWith('/beauty-salon-al-rawdah')) return 'location-al-rawdah';
  if (path.startsWith('/salon-near-adnec')) return 'location-adnec';
  if (path.startsWith('/beauty-salon-aloft')) return 'location-aloft';
  if (path.startsWith('/about')) return 'about';
  if (path.startsWith('/gallery')) return 'gallery';
  if (path.startsWith('/blog/')) return 'blog-post';
  if (path.startsWith('/blog')) return 'blog';
  if (path.startsWith('/book')) return 'booking';
  return 'home';
};


const ensureMetaTag = (selector: string, attrs: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const ensureLinkTag = (selector: string, attrs: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertJsonLd = (id: string, data: Record<string, unknown>) => {
  let script = document.head.querySelector(`#${id}`) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
};

import { BLOG_POSTS } from './blogData';

const blogPostSchema = (post: any, siteUrl: string, currentUrl: string) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt,
  image: post.image,
  datePublished: post.date,
  author: {
    '@type': 'Person',
    name: post.author,
  },
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    logo: {
      '@type': 'ImageObject',
      url: new URL(DEFAULT_IMAGE, siteUrl).toString(),
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': currentUrl,
  },
});

const pageSchemas = (view: ViewState, siteUrl: string, currentUrl: string, blogSlug?: string) => {
  const shared = [websiteSchema(siteUrl), organizationSchema(siteUrl), beautySalonSchema(siteUrl)];

  if (view === 'blog-post' && blogSlug) {
    const post = BLOG_POSTS.find(p => p.slug === blogSlug);
    if (post) {
      return [
        ...shared,
        blogPostSchema(post, siteUrl, currentUrl),
        breadcrumbSchema(
          [
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: `/blog/${post.slug}` },
          ],
          siteUrl,
        ),
      ];
    }
  }

  if (view === 'home') {
    return [
      ...shared,
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Luxury Beauty Salon Abu Dhabi',
        url: currentUrl,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['[data-speakable="hero-summary"]', '[data-speakable="faq-answer"]'],
        },
      },
      breadcrumbSchema([{ name: 'Home', path: '/' }], siteUrl),
      faqSchema(homeFaq),
    ];
  }

  if (view === 'services') {
    return [
      ...shared,
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Beauty Salon Services Abu Dhabi',
        url: currentUrl,
      },
      breadcrumbSchema(
        [
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
        ],
        siteUrl,
      ),
      faqSchema(servicesFaq),
    ];
  }

  if (view === 'about') {
    return [
      ...shared,
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'About MIER Beauty Lounge',
        url: currentUrl,
      },
      breadcrumbSchema(
        [
          { name: 'Home', path: '/' },
          { name: 'About', path: '/about' },
        ],
        siteUrl,
      ),
      faqSchema(aboutFaq),
    ];
  }

  if (view === 'gallery') {
    return [
      ...shared,
      {
        '@context': 'https://schema.org',
        '@type': 'ImageGallery',
        name: 'MIER Beauty Lounge Gallery',
        url: currentUrl,
      },
      breadcrumbSchema(
        [
          { name: 'Home', path: '/' },
          { name: 'Gallery', path: '/gallery' },
        ],
        siteUrl,
      ),
    ];
  }

  return [
    ...shared,
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Book an Appointment',
      url: currentUrl,
    },
    breadcrumbSchema(
      [
        { name: 'Home', path: '/' },
        { name: 'Book Online', path: '/book' },
      ],
      siteUrl,
    ),
  ];
};

export function Seo({ view, blogSlug }: { view: ViewState; blogSlug?: string }) {
  useEffect(() => {
    const siteUrl = window.location.origin || DEFAULT_SITE_URL;
    const path = pathForView(view);
    const currentUrl = new URL(path, siteUrl).toString();
    const config = { ...routeSeo[view] };

    // Override with dynamic blog content if applicable
    if (view === 'blog-post' && blogSlug) {
      const post = BLOG_POSTS.find(p => p.slug === blogSlug);
      if (post) {
        config.title = `${post.title} | ${SITE_NAME}`;
        config.description = post.excerpt;
      }
    }

    document.documentElement.lang = 'en-AE';
    document.title = config.title;

    ensureMetaTag('meta[name="description"]', { name: 'description', content: config.description });
    ensureMetaTag('meta[name="keywords"]', { name: 'keywords', content: config.keywords });
    
    // Arabic Meta Tags for Local AEO/GEO
    if (config.titleAr) ensureMetaTag('meta[name="title-ar"]', { name: 'title-ar', content: config.titleAr });
    if (config.descriptionAr) ensureMetaTag('meta[name="description-ar"]', { name: 'description-ar', content: config.descriptionAr });
    if (config.keywordsAr) ensureMetaTag('meta[name="keywords-ar"]', { name: 'keywords-ar', content: config.keywordsAr });

    ensureMetaTag('meta[name="robots"]', { name: 'robots', content: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1' });
    ensureMetaTag('meta[property="og:title"]', { property: 'og:title', content: config.title });
    ensureMetaTag('meta[property="og:description"]', { property: 'og:description', content: config.description });
    ensureMetaTag('meta[property="og:type"]', { property: 'og:type', content: config.ogType ?? 'website' });
    ensureMetaTag('meta[property="og:url"]', { property: 'og:url', content: currentUrl });
    ensureMetaTag('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    ensureMetaTag('meta[property="og:image"]', { property: 'og:image', content: new URL(DEFAULT_IMAGE, siteUrl).toString() });
    ensureMetaTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    ensureMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: config.title });
    ensureMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: config.description });
    ensureMetaTag('meta[name="geo.region"]', { name: 'geo.region', content: 'AE-AZ' });
    ensureMetaTag('meta[name="geo.placename"]', { name: 'geo.placename', content: 'Abu Dhabi' });

    ensureLinkTag('link[rel="canonical"]', { rel: 'canonical', href: currentUrl });
    ensureLinkTag('link[rel="alternate"][hreflang="en-AE"]', { rel: 'alternate', hreflang: 'en-AE', href: currentUrl });
    ensureLinkTag('link[rel="alternate"][hreflang="ar-AE"]', { rel: 'alternate', hreflang: 'ar-AE', href: currentUrl });
    ensureLinkTag('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: currentUrl });

    const schemas = pageSchemas(view, siteUrl, currentUrl, blogSlug);
    schemas.forEach((schema, index) => upsertJsonLd(`seo-schema-${index}`, schema));
    const existing = document.head.querySelectorAll('script[id^="seo-schema-"]');
    existing.forEach((node) => {
      const index = Number(node.id.replace('seo-schema-', ''));
      if (Number.isFinite(index) && index >= schemas.length) {
        node.remove();
      }
    });
  }, [view, blogSlug]);

  return null;
}
